import { useCallback, useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { confirmDialog } from "../lib/uiDialogs";
import {
  Exam,
  Play,
  CheckCircle,
  XCircle,
  ArrowRight,
  ArrowLeft,
  Trophy,
  Clock,
  SpinnerGap,
  VideoCamera,
  Lightning,
  Target,
  ChartBar,
  Star,
  ArrowClockwise,
  Sparkle
} from "@phosphor-icons/react";
import { useLang } from "../contexts/LanguageContext";

import { API_BASE as API } from "../lib/apiBase";

const DIFFICULTY_STYLES = {
  easy: "bg-blue-100 text-blue-700",
  medium: "bg-yellow-100 text-yellow-700",
  hard: "bg-red-100 text-red-700",
};

export default function QuizPage() {
  const [phase, setPhase] = useState("select"); // select | quiz | results | history | exam
  const { lang, t, tr } = useLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  // Vincent #343 (Eric 2026-06-18 SPEC STRICK ULTRA) — Mémorisation de la page
  // d'origine pour pouvoir revenir au parcours.
  const goBackToOrigin = () => {
    // Tente d'utiliser l'historique du navigateur ; fallback : page parcours.
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/dashboard/onboarding");
    }
  };
  const [themes, setThemes] = useState([]);
  // Tâche 1484 — Rappel discret pour les concepteurs : les fautes que les
  // apprenants tapent et qui ne sont pas encore au catalogue. L'endpoint est
  // réservé aux concepteurs ; pour un apprenant il répond 403 et le rappel
  // n'apparaît tout simplement pas.
  const [fautesATraiter, setFautesATraiter] = useState(0);
  useEffect(() => {
    axios.get(`${API}/api/orthographe/fautes-frequentes?limite=50`, { withCredentials: true })
      .then((r) => setFautesATraiter(r.data?.a_traiter || 0))
      .catch(() => setFautesATraiter(0));
  }, []);
  const [selectedTheme, setSelectedTheme] = useState("all");
  // Vincent #389 — numQuestions est déclaré plus bas (après initialScope) pour
  // présélectionner 7 quand ?pathway_id est dans l'URL.
  const [quiz, setQuiz] = useState(null);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [history, setHistory] = useState([]);
  // Vincent B555 (Eric 2026-07-25) — Détail d'un quiz de l'historique.
  // Modal avec toutes les Q&R : question, réponse choisie, bonne réponse,
  // explication, source (vidéo/parcours), score. Le chargement est lazy
  // (GET /api/quiz/history/{id}) pour ne pas surcharger la liste.
  const [historyDetail, setHistoryDetail] = useState(null); // full result_doc
  const [historyDetailLoading, setHistoryDetailLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [examMode, setExamMode] = useState(false);
  // Vincent #327 (Eric 2026-06-17 SPEC STRICK) — Par défaut "Aucune limite" (0)
  const [timeLimit, setTimeLimit] = useState(0);
  const [timeLeft, setTimeLeft] = useState(null);
  const [examStarted, setExamStarted] = useState(false);
  // iter149b — Timestamp de démarrage de l'examen (ms Unix), pour calculer
  // le temps réellement écoulé à la soumission et le persister avec les
  // résultats (« Rythme utilisé » affiché dans l'historique).
  const [examStartedAt, setExamStartedAt] = useState(null);
  // Vincent #378/#379 (Eric 2026-06-23 SPEC STRICK) — Renommage "Vidéo unique"
  // → "Document unique" (Vincent demande explicitement le terme générique pour
  // englober vidéos ET fichiers textes). Présélections contextuelles :
  //   - ?video_id=X → "Document unique" présélectionné
  //   - ?pathway_id=X → "Parcours" présélectionné
  //   - Sinon → "Tous les sujets"
  const initialScope = (() => {
    if (searchParams.get("video_id")) return "video";
    if (searchParams.get("pathway_id")) return "pathway";
    return "all";
  })();
  const [quizScope, setQuizScope] = useState(initialScope);
  // Vincent #389 (Eric 2026-06-29 SPEC STRICK) — Quand on arrive avec ?pathway_id,
  // présélectionne 7 questions par défaut (au lieu de 5) ; le bouton "3 questions"
  // sera désactivé plus bas pour les quiz de parcours.
  const [numQuestions, setNumQuestions] = useState(initialScope === "pathway" ? 7 : 5);
  const [pathwaysList, setPathwaysList] = useState([]);
  const [selectedPathwayId, setSelectedPathwayId] = useState(searchParams.get("pathway_id") || "");
  const [selectedVideoId, setSelectedVideoId] = useState(searchParams.get("video_id") || "");
  // Bug 537 (Eric 2026-07-24) — Champ texte libre + résultat de la vérif KB.
  const [customTheme, setCustomTheme] = useState("");
  const [customThemeCheck, setCustomThemeCheck] = useState(null);
  const [customThemeChecking, setCustomThemeChecking] = useState(false);
  // Tâche 1479 — Vrai quand l'utilisateur a demandé à chercher SA graphie.
  const [sansCorrection, setSansCorrection] = useState(false);

  useEffect(() => {
    loadThemes();
    loadHistory();
    // Vincent #366/#367 — Charge la liste des parcours pour le dropdown.
    axios.get(`${API}/api/pathways`, { withCredentials: true })
      .then(r => setPathwaysList(r.data?.pathways || r.data || []))
      .catch(() => setPathwaysList([]));
  }, []);

  // Vincent #367 — Quand on vient avec video_id, pré-sélectionner aussi le
  // parcours qui contient cette vidéo (pour pré-remplir le dropdown "Parcours").
  useEffect(() => {
    const vidFromUrl = searchParams.get("video_id");
    if (vidFromUrl && pathwaysList.length > 0 && !selectedPathwayId) {
      const found = pathwaysList.find(p => (p.video_ids || []).includes(vidFromUrl));
      if (found) setSelectedPathwayId(found.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathwaysList]);

  // Liste des vidéos disponibles dans le dropdown "Vidéo unique" : celles du
  // parcours sélectionné (ou toutes si "Tous les sujets").
  const videosInScope = (() => {
    if (!selectedPathwayId) return [];
    const p = pathwaysList.find(x => x.id === selectedPathwayId);
    return p?.videos || [];
  })();

  // Vincent #262/#269 (Eric 2026-02-06) — Si la query string contient
  // ?video_id=X, on génère un quiz STRICTEMENT lié à cette vidéo. Sinon, on
  // utilise le thème comme avant.
  useEffect(() => {
    const themeParam = searchParams.get("theme");
    const videoIdParam = searchParams.get("video_id");
    // Vincent #15 (XLS 2026-06-17) — Ne PLUS auto-lancer le quiz. L'utilisateur
    // doit pouvoir configurer (nombre de questions, time_limit, exam_mode)
    // AVANT chaque génération. Le paramètre auto=1 est désormais ignoré.
    if (videoIdParam && phase === "select") {
      // Le video_id reste dans l'URL pour info ; quand l'utilisateur cliquera
      // "Démarrer le quiz", handleGenerateForVideo sera appelé avec ce paramètre.
      return;
    }
    if (themeParam && themes.length > 0) {
      const match = themes.find(t => t.theme === themeParam || t.theme?.toLowerCase() === themeParam.toLowerCase());
      const themeToUse = match ? match.theme : "all";
      setSelectedTheme(themeToUse);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themes]);

  const handleGenerateForVideo = async (videoId) => {
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/api/quiz/generate`,
        { video_id: videoId, num_questions: numQuestions },
        { withCredentials: true }
      );
      setQuiz(data);
      setCurrentIdx(0);
      setAnswers({});
      setResults(null);
      setPhase("quiz");
    } catch (err) {
      toast.error(err.response?.data?.detail || tr("Pas assez de connaissances approuvées sur cette vidéo pour générer un quiz. Demande à un admin de valider plus de Q&R.", "Not enough approved knowledge on this video to generate a quiz. Ask an admin to validate more Q&A."));
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateForTheme = async (themeArg) => {
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/api/quiz/generate`,
        { theme: themeArg, num_questions: numQuestions },
        { withCredentials: true }
      );
      setQuiz(data);
      setCurrentIdx(0);
      setAnswers({});
      setResults(null);
      setPhase("quiz");
    } catch (err) {
      toast.error(err.response?.data?.detail || tr("Erreur de génération", "Generation failed"));
    } finally {
      setLoading(false);
    }
  };

  const loadThemes = async () => {
    try {
      const { data } = await axios.get(`${API}/api/quiz/themes`, { withCredentials: true });
      setThemes(data);
    } catch (err) {
      console.error("Failed to load themes:", err);
    }
  };

  const loadHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/api/quiz/history`, { withCredentials: true });
      setHistory(data);
    } catch (err) {
      console.error("Failed to load history:", err);
    }
  };

  const handleGenerate = async () => {
    // Vincent #366/#367 (Eric 2026-06-22 SPEC STRICK) — Choix de portée explicite
    // via boutons "Tous les sujets" / "Parcours" / "Vidéo unique".
    if (quizScope === "video") {
      const vid = selectedVideoId || searchParams.get("video_id");
      if (!vid) {
        toast.error(lang === "fr" ? "Choisissez une vidéo dans le menu déroulant." : "Choose a video from the dropdown.");
        return;
      }
      return handleGenerateForVideo(vid);
    }
    if (quizScope === "pathway") {
      const pid = selectedPathwayId || searchParams.get("pathway_id");
      if (!pid) {
        toast.error(lang === "fr" ? "Choisissez un parcours dans le menu déroulant." : "Choose a pathway from the dropdown.");
        return;
      }
      setLoading(true);
      try {
        const { data } = await axios.post(
          `${API}/api/quiz/generate`,
          { pathway_id: pid, num_questions: numQuestions },
          { withCredentials: true }
        );
        setQuiz(data);
        setCurrentIdx(0);
        setAnswers({});
        setResults(null);
        setPhase("quiz");
      } catch (err) {
        toast.error(err.response?.data?.detail || tr("Erreur de génération", "Generation failed"));
      } finally {
        setLoading(false);
      }
      return;
    }
    // scope === "all" : ancien comportement par thème
    // Bug 537 (Eric 2026-07-24) — Si un thème libre a été saisi ET vérifié
    // avec succès (sufficient=true), on l'envoie via custom_theme et on
    // ignore selectedTheme. La vérification `sufficient` évite un appel
    // GPT coûteux quand la KB n'a rien.
    const useCustomTheme = customTheme.trim() && customThemeCheck?.sufficient;
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/api/quiz/generate`,
        useCustomTheme
          ? { custom_theme: customTheme.trim(), num_questions: numQuestions, sans_correction: sansCorrection }
          : { theme: selectedTheme, num_questions: numQuestions },
        { withCredentials: true }
      );
      setQuiz(data);
      setCurrentIdx(0);
      setAnswers({});
      setResults(null);
      setPhase("quiz");
    } catch (err) {
      toast.error(err.response?.data?.detail || tr("Erreur de génération", "Generation failed"));
    } finally {
      setLoading(false);
    }
  };

  // Bug 537 (Eric 2026-07-24) — Vérifie que la KB contient assez de contenu
  // pour générer un quiz sur le thème saisi. Bloque la génération sinon avec
  // un message contextualisé.
  // Tâche 1479 (Eric) — `sansCorrection` est le « chercher quand même ma
  // graphie » : la correction orthographique est utile, mais elle ne doit
  // jamais être imposée sans échappatoire.
  const handleCheckCustomTheme = async (sansCorrection = false) => {
    const q = customTheme.trim();
    if (q.length < 3) return;
    setCustomThemeChecking(true);
    setSansCorrection(sansCorrection);
    try {
      const { data } = await axios.post(
        `${API}/api/quiz/check-custom-theme`,
        { custom_theme: q, sans_correction: sansCorrection },
        { withCredentials: true }
      );
      setCustomThemeCheck(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === "fr" ? "Erreur de vérification" : "Check error"));
    } finally {
      setCustomThemeChecking(false);
    }
  };

  const handleSelectOption = (questionId, optionIdx) => {
    setAnswers((prev) => ({ ...prev, [questionId]: optionIdx }));
  };

  // Vincent B565 (Eric 2026-07-25) — Soumission normale (bloque si des
  // questions sont sans réponse) OU soumission FORCÉE (utilisée par le timer
  // à l'expiration du chronomètre : accepte les questions sans réponse en les
  // marquant comme incorrectes, `selected_option = -1`). Sans le flag `force`,
  // le quiz ne se terminait JAMAIS quand le temps était écoulé mais que
  // l'utilisateur n'avait pas répondu à tout.
  const handleSubmit = useCallback(async (force = false) => {
    if (!quiz) return;
    const unanswered = quiz.questions.filter((q) => answers[q.id] === undefined);
    if (unanswered.length > 0 && !force) {
      toast.error(`${unanswered.length} question(s) sans réponse`);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        answers: quiz.questions.map((q) => ({
          question_id: q.id,
          // Vincent B565 — En mode force (timer expiré), les questions sans
          // réponse sont envoyées avec selected_option = -1 (sentinelle « pas
          // répondu ») pour que le backend les compte comme incorrectes.
          selected_option: answers[q.id] !== undefined ? answers[q.id] : -1,
        })),
        // iter149b — Temps réellement écoulé côté client, pour afficher le
        // rythme utilisé dans le détail de l'historique. Null si pas mode Examen.
        time_used_seconds: examStartedAt ? (Date.now() - examStartedAt) / 1000 : null,
      };
      const { data } = await axios.post(
        `${API}/api/quiz/${quiz.quiz_id}/submit`,
        payload,
        { withCredentials: true }
      );
      setResults(data);
      setPhase("results");
      if (force && unanswered.length > 0) {
        toast.warning(
          `Temps écoulé — soumission automatique. ${unanswered.length} question(s) sans réponse comptée(s) incorrecte(s).`,
          { duration: 6000 }
        );
      }
      loadHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || tr("Erreur de soumission", "Submission failed"));
    } finally {
      setSubmitting(false);
    }
  }, [quiz, answers, examStartedAt]);

  const resetQuiz = () => {
    setQuiz(null);
    setAnswers({});
    setResults(null);
    setCurrentIdx(0);
    setPhase("select");
    setExamMode(false);
    setTimeLeft(null);
    setExamStarted(false);
  };

  // Exam timer — Vincent B565 (Eric 2026-07-25) : à l'expiration du temps,
  // soumission FORCÉE (accepte les questions sans réponse, les marque -1).
  useEffect(() => {
    if (!examStarted || timeLeft === null || timeLeft <= 0) return;
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmit(true); // force=true → soumission auto même si incomplet
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [examStarted, timeLeft, handleSubmit]);

  const handleGenerateExam = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/api/quiz/generate-exam`,
        { theme: selectedTheme === "all" ? "general" : selectedTheme, num_questions: numQuestions, time_limit: timeLimit },
        { withCredentials: true }
      );
      setQuiz({ quiz_id: data.exam_id, questions: data.questions, theme: data.theme });
      setCurrentIdx(0);
      setAnswers({});
      setResults(null);
      setExamMode(true);
      setTimeLeft(data.time_limit || timeLimit);
      setExamStarted(true);
      setExamStartedAt(Date.now()); // iter149b — pour calculer time_used
      setPhase("quiz");
    } catch (err) {
      toast.error(err.response?.data?.detail || tr("Erreur de génération d'examen", "Exam generation failed"));
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  // ==================== SELECT PHASE ====================
  if (phase === "select") {
    return (
      <div className="animate-fade-in max-w-4xl mx-auto">
        {/* Vincent #343 (Eric 2026-06-18 SPEC STRICK ULTRA) — Bouton "Retour" en haut à gauche */}
        <button
          onClick={goBackToOrigin}
          className="mb-3 inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-[#002FA7] transition-colors"
          data-testid="quiz-back-btn-select"
        >
          <ArrowLeft size={16} /> Retour
        </button>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight font-['Outfit']" data-testid="quiz-page-title">
              {tr("Quiz d'évaluation", "Assessment quiz")}
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              {tr("Testez vos connaissances à partir de la base extraite", "Test your knowledge against the extracted knowledge base")}
            </p>
          </div>
          {history.length > 0 && (
            <Button
              variant="outline"
              onClick={() => setPhase("history")}
              data-testid="view-history-btn"
            >
              <Clock size={16} className="mr-2" />
              {t("quiz_history")} ({history.length})
            </Button>
          )}
        </div>

        {/* Vincent #366/#367 (Eric 2026-06-22 SPEC STRICK) — 3 portées de quiz :
            "Tous les sujets" · "Parcours" · "Vidéo unique". La portée est
            pré-sélectionnée selon les paramètres URL (video_id → Vidéo unique ;
            pathway_id → Parcours ; sinon Tous les sujets). */}
        <div className="bg-white border border-zinc-200 rounded-sm p-6 mb-6" data-testid="quiz-scope-selector">
          <h3 className="font-medium mb-4">{lang === "fr" ? "Portée du quiz" : "Quiz scope"}</h3>
          <div className="grid grid-cols-3 gap-3 mb-4">
            {[
              { key: "all", label: lang === "fr" ? "Tous les sujets" : "All topics", testid: "scope-all" },
              { key: "pathway", label: lang === "fr" ? "Parcours" : "Pathway", testid: "scope-pathway" },
              { key: "video", label: lang === "fr" ? "Document unique" : "Single document", testid: "scope-video" },
            ].map(opt => (
              <button
                key={opt.key}
                onClick={() => setQuizScope(opt.key)}
                className={`p-3 rounded-sm border text-sm font-semibold transition-all ${
                  quizScope === opt.key
                    ? "border-[#002FA7] bg-blue-50 ring-2 ring-[#002FA7]/20 text-[#002FA7]"
                    : "border-zinc-200 hover:border-zinc-400 text-zinc-700"
                }`}
                data-testid={opt.testid}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {quizScope === "pathway" && (
            <div data-testid="scope-pathway-dropdown-section">
              <label className="text-xs font-medium text-zinc-600 mb-1 block">
                {lang === "fr" ? "Parcours" : "Pathway"}
              </label>
              <select
                value={selectedPathwayId}
                onChange={(e) => setSelectedPathwayId(e.target.value)}
                className="w-full px-3 py-2 border border-zinc-300 rounded text-sm bg-white"
                data-testid="scope-pathway-dropdown"
              >
                <option value="">{lang === "fr" ? "— Choisir un parcours —" : "— Choose a pathway —"}</option>
                {/* Bug 538 (Eric 2026-07-24) — Tri alphabétique croissant
                    stable, insensible aux ajouts/retraits dans le temps. */}
                {[...pathwaysList]
                  .sort((a, b) => (a.name || a.title || "").localeCompare(b.name || b.title || "", "fr", { sensitivity: "base" }))
                  .map(p => (
                  <option key={p.id} value={p.id}>{p.name || p.title || p.id}</option>
                ))}
              </select>
            </div>
          )}

          {quizScope === "video" && (
            <div className="space-y-3" data-testid="scope-video-dropdown-section">
              {/* Vincent #379 (Eric 2026-06-23 SPEC STRICK) — Quand "Document unique"
                  est sélectionné, on affiche DEUX menus déroulants :
                  1. Parcours (parmi ceux auxquels l'utilisateur est associé)
                  2. Document du parcours choisi
                  Pas de message d'erreur si pas de parcours choisi : on guide. */}
              <div>
                <label className="text-xs font-medium text-zinc-600 mb-1 block">
                  {lang === "fr" ? "Parcours" : "Pathway"}
                </label>
                <select
                  value={selectedPathwayId}
                  onChange={(e) => { setSelectedPathwayId(e.target.value); setSelectedVideoId(""); }}
                  className="w-full px-3 py-2 border border-zinc-300 rounded text-sm bg-white"
                  data-testid="scope-video-pathway-dropdown"
                >
                  <option value="">{lang === "fr" ? "— Choisir un parcours —" : "— Choose a pathway —"}</option>
                  {/* Bug 538 (Eric 2026-07-24) — Tri alphabétique croissant. */}
                  {[...pathwaysList]
                    .sort((a, b) => (a.name || a.title || "").localeCompare(b.name || b.title || "", "fr", { sensitivity: "base" }))
                    .map(p => (
                    <option key={p.id} value={p.id}>{p.name || p.title || p.id}</option>
                  ))}
                </select>
              </div>
              {selectedPathwayId && (
                <div>
                  <label className="text-xs font-medium text-zinc-600 mb-1 block">
                    {lang === "fr" ? "Document du parcours" : "Pathway document"}
                  </label>
                  <select
                    value={selectedVideoId}
                    onChange={(e) => setSelectedVideoId(e.target.value)}
                    className="w-full px-3 py-2 border border-zinc-300 rounded text-sm bg-white"
                    data-testid="scope-video-dropdown"
                  >
                    <option value="">{lang === "fr" ? "— Choisir un document —" : "— Choose a document —"}</option>
                    {/* Bug 538 (Eric 2026-07-24) — Tri alphabétique croissant
                        sur le titre (ou nom de fichier nettoyé). */}
                    {[...videosInScope]
                      .sort((a, b) => {
                        const ta = a.title || (a.original_filename || "").replace(/\.[^.]+$/, "").replace(/_/g, " ").trim() || "";
                        const tb = b.title || (b.original_filename || "").replace(/\.[^.]+$/, "").replace(/_/g, " ").trim() || "";
                        return ta.localeCompare(tb, "fr", { sensitivity: "base" });
                      })
                      .map(v => (
                      <option key={v.id} value={v.id}>
                        {v.title || (v.original_filename || "").replace(/\.[^.]+$/, "").replace(/_/g, " ").trim() || v.id}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Thème selection (visible uniquement quand portée = Tous les sujets) */}
        {quizScope === "all" && (
        <div className="bg-white border border-zinc-200 rounded-sm p-6 mb-6" data-testid="theme-dropdown-section">
          <h3 className="font-medium mb-4">
            {lang === "fr" ? "Choisir un thème" : "Choose a theme"}
          </h3>
          {/* Vincent #375 (Eric 2026-06-23 SPEC STRICK) — Remplacer les 4 boutons
              par un menu déroulant unique avec les thèmes selon ce qui est défini
              en français pour organiser les fichiers (Explorateur + Catégories). */}
          <select
            value={selectedTheme}
            onChange={(e) => setSelectedTheme(e.target.value)}
            className="w-full px-3 py-2 border border-zinc-300 rounded text-sm bg-white"
            data-testid="theme-dropdown"
          >
            {/* Bug 538 (Eric 2026-07-24) — Tri alphabétique croissant sur
                le label affiché (permet un tri utilisateur-cohérent même si
                la clé interne `theme` est en anglais/technique).
                Bug 541 (Eric 2026-07-24) — Marque avec 🔒 les thèmes non
                accessibles à l'utilisateur (`accessible=false`), sans les
                cacher (transparence sur le contenu à venir). L'option
                reste sélectionnable — le message d'info s'affiche au
                clic « Démarrer ». */}
            {[...themes]
              .sort((a, b) => (a.label || a.theme || "").localeCompare(b.label || b.theme || "", "fr", { sensitivity: "base" }))
              .map((t) => {
              const inaccessible = t.accessible === false;
              return (
              <option key={t.theme} value={t.theme} data-testid={`theme-option-${t.theme}`}>
                {inaccessible ? "🔒 " : ""}{t.label} {t.count > 0 ? `(${t.count} ${t.count > 1 ? tr("unités", "units") : tr("unité", "unit")})` : ""}{inaccessible ? tr(" — abordé plus tard", " — covered later") : ""}
              </option>
              );
            })}
          </select>
          {/* Bug 541 (Eric 2026-07-24) — Message d'info si le thème
              sélectionné est marqué non-accessible pour le rôle du user
              (via le flag `accessible` renvoyé par /api/quiz/themes). */}
          {(() => {
            const sel = themes.find(t => t.theme === selectedTheme);
            if (sel && sel.accessible === false) {
              return (
                <div className="mt-3 flex items-start gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs" data-testid="theme-inaccessible-info">
                  <span aria-hidden="true">💡</span>
                  <span>
                    {tr("Ce thème sera abordé plus tard dans ton parcours d'apprentissage. Aucun quiz n'est encore disponible pour toi sur cette catégorie — choisis un autre thème (ou « Tous les sujets ») pour continuer.",
                        "This topic comes later in your learning pathway. No quiz is available to you yet in this category — pick another topic (or “All topics”) to continue.")}
                  </span>
                </div>
              );
            }
            return null;
          })()}
        </div>
        )}

        {/* Bug 537 (Eric 2026-07-24) — Champ texte libre pour créer un quiz
            sur un thème quelconque saisi au clavier. Le backend vérifie via
            /api/quiz/check-custom-theme si la KB contient assez de contenu
            (≥ 2 KU approuvées) avant d'autoriser la génération. */}
        {quizScope === "all" && (
        <div className="bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200 rounded-sm p-6 mb-6" data-testid="custom-theme-section">
          <h3 className="font-medium mb-1 flex items-center gap-2 text-violet-900">
            <Sparkle size={16} className="text-violet-600" weight="fill" />
            {lang === "fr" ? "Ou saisis un thème personnalisé" : "Or type a custom theme"}
          </h3>
          <p className="text-xs text-violet-700 mb-3">
            {lang === "fr"
              ? "Tape un mot-clé (ex : « objections », « recrutement »). On vérifie si la base de connaissances a assez de contenu approuvé pour générer un quiz."
              : "Type a keyword (e.g., \"objections\", \"recruiting\"). We check whether the knowledge base has enough approved content to generate a quiz."}
          </p>
          {/* Tâche 1484 — Rappel discret, concepteurs seulement. */}
          {fautesATraiter > 0 && (
            <p className="text-[11px] text-violet-700 mb-3" data-testid="quiz-rappel-fautes">
              {tr(
                `${fautesATraiter} faute(s) d'orthographe tapée(s) par les apprenants ne sont pas encore au catalogue — `,
                `${fautesATraiter} misspelling(s) typed by learners are not in the catalogue yet — `
              )}
              <button
                type="button"
                onClick={() => navigate("/dashboard/orthographe")}
                className="underline underline-offset-2 font-semibold hover:text-violet-900"
                data-testid="quiz-rappel-fautes-lien"
              >
                {tr("les voir dans Orthographe", "see them in Spelling")}
              </button>
            </p>
          )}
          <div className="flex gap-2">
            <input
              type="text"
              value={customTheme}
              onChange={(e) => { setCustomTheme(e.target.value); setCustomThemeCheck(null); setSansCorrection(false); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleCheckCustomTheme(false); } }}
              placeholder={lang === "fr" ? "Ex : recrutement, objection prix, planification retraite…" : "e.g., recruiting, price objection, retirement planning…"}
              maxLength={100}
              className="flex-1 px-3 py-2 border border-violet-300 rounded text-sm bg-white focus:outline-none focus:ring-2 focus:ring-violet-500"
              data-testid="custom-theme-input"
            />
            <button
              type="button"
              onClick={() => handleCheckCustomTheme(false)}
              disabled={!customTheme.trim() || customThemeChecking || customTheme.trim().length < 3}
              className="px-4 py-2 rounded bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="custom-theme-check-btn"
            >
              {customThemeChecking ? "…" : (lang === "fr" ? "Vérifier" : "Check")}
            </button>
          </div>
          {/* Tâche 1479 (Eric) — La correction d'orthographe est DITE, avec le
              moyen de chercher la graphie d'origine : corriger en silence
              reviendrait à chercher autre chose que ce qui a été demandé. */}
          {customThemeCheck?.correction?.corrige && (
            <div
              className="mt-3 px-3 py-2 rounded-md text-xs border bg-sky-50 border-sky-200 text-sky-900"
              data-testid="custom-theme-correction"
            >
              <div>
                {tr("Recherche sur", "Searching for")}{" "}
                <strong data-testid="custom-theme-correction-corrigee">
                  {customThemeCheck.correction.requete}
                </strong>{" "}
                —{" "}
                {tr("corrigé de", "corrected from")}{" "}
                <em data-testid="custom-theme-correction-origine">
                  « {customThemeCheck.correction.origine} »
                </em>
                {customThemeCheck.correction.source === "ia"
                  ? tr(" (correction par l'IA)", " (AI correction)")
                  : ""}
              </div>
              <button
                type="button"
                onClick={() => handleCheckCustomTheme(true)}
                className="mt-1 underline underline-offset-2 hover:text-sky-700"
                data-testid="custom-theme-search-original-btn"
              >
                {tr(
                  `Chercher quand même « ${customThemeCheck.correction.origine} »`,
                  `Search « ${customThemeCheck.correction.origine} » anyway`,
                )}
              </button>
            </div>
          )}
          {sansCorrection && !customThemeCheck?.correction?.corrige && (
            <div
              className="mt-3 px-3 py-2 rounded-md text-xs border bg-zinc-50 border-zinc-200 text-zinc-600"
              data-testid="custom-theme-sans-correction"
            >
              {tr("Recherche sur votre graphie exacte, sans correction.",
                  "Searching your exact spelling, without correction.")}
            </div>
          )}
          {customThemeCheck && (
            <div
              className={`mt-3 px-3 py-2 rounded-md text-xs border ${
                customThemeCheck.sufficient
                  ? "bg-teal-50 border-teal-200 text-teal-800"
                  : "bg-amber-50 border-amber-200 text-amber-800"
              }`}
              data-testid="custom-theme-check-result"
            >
              {customThemeCheck.sufficient ? (
                <>
                  <div className="font-semibold mb-1">
                    ✅ {customThemeCheck.count}{" "}
                    {customThemeCheck.count > 1
                      ? tr("unités de connaissance trouvées sur", "knowledge units found on")
                      : tr("unité de connaissance trouvée sur", "knowledge unit found on")}{" "}
                    « {customThemeCheck.custom_theme} »
                  </div>
                  {customThemeCheck.sample_titles?.length > 0 && (
                    <ul className="ml-4 mt-1 space-y-0.5 text-teal-700">
                      {customThemeCheck.sample_titles.slice(0, 3).map((s, i) => (
                        <li key={i}>• {s.title}</li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-2 font-medium">
                    {tr("Le quiz sera généré à partir de ces unités. Choisis le nombre de questions ci-dessous puis clique « Démarrer ».",
                        "The quiz will be generated from these units. Choose the number of questions below, then click “Start”.")}
                  </div>
                </>
              ) : (
                <>
                  <div className="font-semibold mb-1">
                    ⚠️ {tr("Pas encore assez de contenu sur", "Not enough content yet on")} « {customThemeCheck.custom_theme} »
                  </div>
                  <div>
                    {tr("La base de connaissances contient", "The knowledge base holds")}{" "}
                    <strong>{customThemeCheck.count}</strong>{" "}
                    {customThemeCheck.count > 1
                      ? tr("unités approuvées", "approved units")
                      : tr("unité approuvée", "approved unit")}{" "}
                    {tr("— il en faut au moins 2 pour générer un quiz. Essaie un autre mot-clé (ex. : un synonyme plus général) ou choisis un thème de la liste ci-dessus.",
                        "— at least 2 are needed to generate a quiz. Try another keyword (for example a broader synonym) or pick a topic from the list above.")}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        )}

        {/* Number of questions — Vincent B564 (Eric 2026-07-25) : ajouté
            8, 12, 15, 20, 25, 30, 40, 50 en plus des 3/5/7/10 existants
            (ordre croissant). Grille flex-wrap pour tenir sur mobile.
            Boutons rétrécis à 12x12 pour 12 boutons sur une ligne desktop. */}
        <div className="bg-white border border-zinc-200 rounded-sm p-6 mb-6">
          <h3 className="font-medium mb-4">{t("num_questions")}</h3>
          <div className="flex gap-2 flex-wrap" data-testid="num-questions-grid">
            {[3, 5, 7, 8, 10, 12, 15, 20, 25, 30, 40, 50].map((n) => {
              // Vincent #389 (Eric 2026-06-29 SPEC STRICK) — Pour un quiz de
              // parcours, on désactive l'option "3 questions" : trop court pour
              // évaluer un parcours complet (min 5).
              const disabled = quizScope === "pathway" && n === 3;
              return (
                <button
                  key={n}
                  onClick={() => !disabled && setNumQuestions(n)}
                  disabled={disabled}
                  className={`w-12 h-12 rounded-sm border font-semibold transition-all text-sm ${
                    numQuestions === n
                      ? "border-[#002FA7] bg-[#002FA7] text-white"
                      : "border-zinc-200 hover:border-zinc-400 text-zinc-600"
                  } ${disabled ? "opacity-40 cursor-not-allowed line-through" : ""}`}
                  data-testid={`num-questions-${n}`}
                  title={disabled ? (lang === "fr" ? "Indisponible pour un quiz de parcours" : "Not available for pathway quiz") : ""}
                >
                  {n}
                </button>
              );
            })}
          </div>
        </div>

        {/* Vincent #327 (Eric 2026-06-17 SPEC STRICK) — Durée du mode Examen.
            Vincent B564 étendu (Eric 2026-07-25) : ajout de 25/30/45/60 min
            pour permettre les quiz jusqu'à 50 questions avec un rythme
            confortable (min 10 s/question, cible ~30-60 s/question). */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-sm p-6 mb-6" data-testid="exam-mode-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Target size={22} className="text-amber-600" />
            </div>
            <div>
              <h3 className="font-bold text-amber-900">{t("exam_mode")}</h3>
              <p className="text-xs text-amber-600">{tr("Durée du quiz — « Aucune limite » pour un quiz sans chronomètre.", "Quiz duration — “No limit” for a quiz without a timer.")}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-full">
              <label className="text-xs font-medium text-amber-800 block mb-1">{tr("Durée limite", "Time limit")}</label>
              <div className="flex gap-2 flex-wrap" data-testid="exam-time-grid">
                {[
                  { label: tr("Aucune limite", "No limit"), value: 0 },
                  { label: "5 min", value: 300 },
                  { label: "10 min", value: 600 },
                  { label: "15 min", value: 900 },
                  { label: "20 min", value: 1200 },
                  { label: "25 min", value: 1500 },
                  { label: "30 min", value: 1800 },
                  { label: "45 min", value: 2700 },
                  { label: "60 min", value: 3600 },
                ].map((tl) => (
                  <button
                    key={tl.value}
                    onClick={() => setTimeLimit(tl.value)}
                    className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${
                      timeLimit === tl.value
                        ? "bg-amber-600 text-white"
                        : "bg-white border border-amber-200 text-amber-700 hover:border-amber-400"
                    }`}
                    data-testid={`exam-time-${tl.value}`}
                  >
                    {tl.label}
                  </button>
                ))}
              </div>
              {/* Vincent 2026-07-25 iter149 — Avertissement quand le rythme
                  demandé est < 10 s/question (impossible en pratique). Vincent :
                  « min 10 s / question ». Affiche aussi la marge en secondes
                  quand un rythme raisonnable (>= 10 s/q) est sélectionné.
                  Palette daltonien-safe (Vincent 2026-07-25) : orange/ambre
                  plutôt que rouge/vert. */}
              {timeLimit > 0 && (() => {
                const secPerQuestion = Math.floor(timeLimit / numQuestions);
                const tooShort = secPerQuestion < 10;
                return (
                  <div
                    className={`mt-3 flex items-start gap-2 text-xs rounded p-2 ${
                      tooShort
                        ? "bg-orange-100 border border-orange-300 text-orange-900"
                        : "bg-amber-100/60 border border-amber-200 text-amber-900"
                    }`}
                    data-testid={tooShort ? "exam-time-warning-too-short" : "exam-time-info"}
                  >
                    <span className="font-bold text-base leading-none">{tooShort ? "⚠" : "⏱"}</span>
                    <span>
                      {tooShort ? (
                        <>
                          <strong>{tr("Rythme trop rapide :", "Pace too fast:")}</strong> {numQuestions}{" "}
                          {tr("questions en", "questions in")}{" "}
                          {Math.floor(timeLimit / 60)} min = <strong>{secPerQuestion} s/question</strong>{" "}
                          {tr("(minimum recommandé : 10 s/question). Choisis une durée plus longue ou réduis le nombre de questions.",
                              "(10 s per question is the recommended minimum). Choose a longer limit or reduce the number of questions.")}
                        </>
                      ) : (
                        <>
                          Rythme prévu : ~<strong>{secPerQuestion} s/question</strong>{" "}
                          ({numQuestions} questions en {Math.floor(timeLimit / 60)} min).
                        </>
                      )}
                    </span>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Vincent #327 (Eric 2026-06-17 SPEC STRICK) — Bouton UNIQUE en bas :
            "Valider et générer le quiz". Si une durée > 0 est sélectionnée,
            on lance le mode Examen ; sinon, quiz classique. */}
        <div className="flex gap-3">
          <Button
            onClick={() => (timeLimit > 0 ? handleGenerateExam() : handleGenerate())}
            disabled={loading}
            className="flex-1 h-14 bg-[#002FA7] hover:bg-[#002280] text-lg"
            data-testid="start-quiz-btn"
          >
            {loading ? (
              <>
                <SpinnerGap size={22} className="animate-spin mr-3" />
                {tr("Génération en cours…", "Generating…")}
              </>
            ) : (
              <>
                <Lightning size={22} className="mr-3" />
                {tr("Valider et générer le quiz", "Confirm and generate the quiz")}
                {timeLimit > 0 && <span className="ml-2 text-sm opacity-80">({formatTime(timeLimit)})</span>}
              </>
            )}
          </Button>
        </div>

        {/* Quick stats */}
        {history.length > 0 && (
          <div className="grid grid-cols-3 gap-4 mt-6">
            <div className="bg-white border border-zinc-200 rounded-sm p-4 text-center">
              <div className="text-2xl font-bold text-[#002FA7]">{history.length}</div>
              <div className="text-xs text-zinc-500">Quiz completes</div>
            </div>
            <div className="bg-white border border-zinc-200 rounded-sm p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">
                {Math.round(history.reduce((a, h) => a + h.score_percentage, 0) / history.length)}%
              </div>
              <div className="text-xs text-zinc-500">{t("average_score")}</div>
            </div>
            <div className="bg-white border border-zinc-200 rounded-sm p-4 text-center">
              <div className="text-2xl font-bold text-amber-600">
                {Math.max(...history.map((h) => h.score_percentage))}%
              </div>
              <div className="text-xs text-zinc-500">{t("best_score")}</div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ==================== QUIZ PHASE ====================
  if (phase === "quiz" && quiz) {
    const question = quiz.questions[currentIdx];
    const totalQ = quiz.questions.length;
    const answeredCount = Object.keys(answers).length;

    return (
      <div className="animate-fade-in max-w-3xl mx-auto">
        {/* Vincent #343 (Eric 2026-06-18 SPEC STRICK ULTRA) — Bouton Retour */}
        <button
          onClick={async () => {
            const ok = await confirmDialog("Quitter ce quiz en cours ?");
            if (!ok) return;
            goBackToOrigin();
          }}
          className="mb-3 inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-[#002FA7] transition-colors"
          data-testid="quiz-back-btn-quiz"
        >
          <ArrowLeft size={16} /> Retour
        </button>
        {/* Exam Timer */}
        {examMode && timeLeft !== null && (
          <div className={`flex items-center justify-between px-4 py-2.5 rounded-lg mb-4 ${
            timeLeft <= 60 ? "bg-red-50 border border-red-200" : timeLeft <= 180 ? "bg-amber-50 border border-amber-200" : "bg-blue-50 border border-blue-200"
          }`} data-testid="exam-timer">
            <div className="flex items-center gap-2">
              <Clock size={18} className={timeLeft <= 60 ? "text-red-600" : timeLeft <= 180 ? "text-amber-600" : "text-blue-600"} weight="bold" />
              <span className={`text-sm font-bold ${timeLeft <= 60 ? "text-red-700" : timeLeft <= 180 ? "text-amber-700" : "text-blue-700"}`}>
                MODE EXAMEN
              </span>
            </div>
            <div className={`text-lg font-mono font-bold ${timeLeft <= 60 ? "text-red-600 animate-pulse" : timeLeft <= 180 ? "text-amber-600" : "text-blue-600"}`}>
              {formatTime(timeLeft)}
            </div>
          </div>
        )}

        {/* Tâche 1480 (Eric) — Le tri sur le thème est DIT : un quiz plus
            court qu'attendu passerait sinon pour une panne. */}
        {quiz.theme_filtre?.filtrable && (
          <div
            className="mb-4 px-3 py-2 rounded-md text-xs border bg-indigo-50 border-indigo-200 text-indigo-900"
            data-testid="quiz-theme-filtre"
          >
            {tr(
              `${totalQ} question(s) sur « ${quiz.theme_filtre.libelle} » retenue(s) · ${quiz.theme_filtre.retenues} connaissance(s) sur le thème parmi ${quiz.theme_filtre.examinees} examinée(s)`,
              `${totalQ} question(s) on “${quiz.theme_filtre.libelle}” kept · ${quiz.theme_filtre.retenues} on-theme knowledge unit(s) out of ${quiz.theme_filtre.examinees} examined`,
            )}
            {quiz.theme_filtre.questions_hors_theme > 0 && (
              <span data-testid="quiz-theme-filtre-rejetees">
                {tr(
                  ` · ${quiz.theme_filtre.questions_hors_theme} question(s) hors thème écartée(s)`,
                  ` · ${quiz.theme_filtre.questions_hors_theme} off-theme question(s) discarded`,
                )}
              </span>
            )}
          </div>
        )}

        {/* Progress bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-zinc-500">
              Question {currentIdx + 1} / {totalQ}
            </span>
            <span className="text-sm text-zinc-500">
              {answeredCount} repondue{answeredCount > 1 ? "s" : ""}
            </span>
          </div>
          <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#002FA7] transition-all duration-300"
              style={{ width: `${((currentIdx + 1) / totalQ) * 100}%` }}
            />
          </div>
        </div>

        {/* Question card */}
        <div className="bg-white border border-zinc-200 rounded-sm p-8 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <span className={`px-2 py-0.5 text-xs font-medium rounded ${DIFFICULTY_STYLES[question.difficulty] || DIFFICULTY_STYLES.medium}`}>
              {question.difficulty}
            </span>
          </div>
          <h2 className="text-lg font-semibold mb-6" data-testid="quiz-question-text">
            {question.question}
          </h2>

          <div className="space-y-3">
            {question.options.map((option, idx) => {
              const isSelected = answers[question.id] === idx;
              return (
                <button
                  key={idx}
                  onClick={() => handleSelectOption(question.id, idx)}
                  className={`w-full text-left p-4 rounded-sm border transition-all ${
                    isSelected
                      ? "border-[#002FA7] bg-blue-50 ring-2 ring-[#002FA7]/20"
                      : "border-zinc-200 hover:border-zinc-400 hover:bg-zinc-50"
                  }`}
                  data-testid={`quiz-option-${idx}`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-7 h-7 rounded-full border-2 flex items-center justify-center flex-shrink-0 text-sm font-semibold ${
                        isSelected
                          ? "border-[#002FA7] bg-[#002FA7] text-white"
                          : "border-zinc-300 text-zinc-500"
                      }`}
                    >
                      {String.fromCharCode(65 + idx)}
                    </div>
                    <span className="text-sm pt-0.5">{option}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => setCurrentIdx(Math.max(0, currentIdx - 1))}
            disabled={currentIdx === 0}
            data-testid="prev-question-btn"
          >
            <ArrowLeft size={16} className="mr-2" />
            {t("back")}
          </Button>

          <div className="flex gap-1">
            {quiz.questions.map((q, i) => (
              <button
                key={q.id}
                onClick={() => setCurrentIdx(i)}
                className={`w-8 h-8 rounded-sm text-xs font-medium transition-colors ${
                  i === currentIdx
                    ? "bg-[#002FA7] text-white"
                    : answers[q.id] !== undefined
                    ? "bg-blue-100 text-blue-700"
                    : "bg-zinc-100 text-zinc-500 hover:bg-zinc-200"
                }`}
                data-testid={`question-nav-${i}`}
              >
                {i + 1}
              </button>
            ))}
          </div>

          {currentIdx < totalQ - 1 ? (
            <Button
              onClick={() => setCurrentIdx(currentIdx + 1)}
              data-testid="next-question-btn"
            >
              Suivante
              <ArrowRight size={16} className="ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="submit-quiz-btn"
            >
              {submitting ? (
                <SpinnerGap size={16} className="animate-spin mr-2" />
              ) : (
                <CheckCircle size={16} className="mr-2" />
              )}
              {t("submit_quiz")}
            </Button>
          )}
        </div>
      </div>
    );
  }

  // ==================== RESULTS PHASE ====================
  if (phase === "results" && results) {
    // Palette daltonien-safe (Vincent 2026-07-25) — même 3 tons que la liste
    // d'historique : bleu (excellent) / ambre (moyen) / orange (faible).
    const scoreColor =
      results.score_percentage >= 80
        ? "text-blue-700"
        : results.score_percentage >= 50
        ? "text-amber-700"
        : "text-orange-700";

    return (
      <div className="animate-fade-in max-w-3xl mx-auto">
        {/* Vincent #343 (Eric 2026-06-18 SPEC STRICK ULTRA) — Bouton Retour pour reprendre le parcours */}
        <button
          onClick={goBackToOrigin}
          className="mb-3 inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-[#002FA7] transition-colors"
          data-testid="quiz-back-btn-results"
        >
          <ArrowLeft size={16} /> Retour au parcours
        </button>
        {/* Score header */}
        <div className="bg-white border border-zinc-200 rounded-sm p-8 mb-6 text-center">
          <Trophy size={48} className={`mx-auto mb-3 ${scoreColor}`} />
          <div className={`text-5xl font-bold ${scoreColor}`} data-testid="quiz-final-score">
            {results.score_percentage}%
          </div>
          <p className="text-zinc-500 mt-2">
            {results.score} / {results.total} reponses correctes
          </p>
          <p className="text-sm text-zinc-400 mt-1">
            {results.score_percentage >= 80
              ? t("quiz_excellent")
              : results.score_percentage >= 50
              ? t("quiz_good")
              : t("quiz_review")}
          </p>
        </div>

        {/* Detailed results */}
        <div className="space-y-4 mb-6">
          {results.results.map((r, idx) => (
            <div
              key={r.question_id}
              className={`bg-white border rounded-sm p-5 ${
                r.is_correct ? "border-blue-200" : "border-red-200"
              }`}
              data-testid={`result-item-${idx}`}
            >
              <div className="flex items-start gap-3 mb-3">
                {r.is_correct ? (
                  <CheckCircle size={24} className="text-blue-600 flex-shrink-0" weight="fill" />
                ) : (
                  <XCircle size={24} className="text-red-600 flex-shrink-0" weight="fill" />
                )}
                <h3 className="font-medium text-sm">{r.question}</h3>
              </div>

              <div className="ml-9 space-y-1.5 mb-3">
                {r.options.map((opt, oi) => (
                  <div
                    key={oi}
                    className={`text-sm px-3 py-1.5 rounded ${
                      oi === r.correct_option
                        ? "bg-blue-50 text-blue-700 font-medium"
                        : oi === r.selected_option && !r.is_correct
                        ? "bg-red-50 text-red-600 line-through"
                        : "text-zinc-500"
                    }`}
                  >
                    {String.fromCharCode(65 + oi)}. {opt}
                    {oi === r.correct_option && " ✓"}
                  </div>
                ))}
              </div>

              {/* Explanation */}
              <div className="ml-9 bg-zinc-50 rounded-sm p-3 mb-2">
                <p className="text-xs text-zinc-600">{r.explanation}</p>
              </div>

              {/* Source link */}
              {r.source?.video_title && (
                <div className="ml-9 flex items-center gap-2 text-xs text-zinc-400">
                  <VideoCamera size={14} />
                  <span>{t("sources")}: {r.source.video_title}</span>
                  {r.source.timestamps?.start && (
                    <span>
                      ({Math.floor(r.source.timestamps.start / 60)}:
                      {String(Math.floor(r.source.timestamps.start % 60)).padStart(2, "0")})
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            onClick={resetQuiz}
            className="flex-1 bg-[#002FA7] hover:bg-[#002280]"
            data-testid="new-quiz-btn"
          >
            <ArrowClockwise size={16} className="mr-2" />
            {t("generate_quiz")}
          </Button>
          <Button
            variant="outline"
            onClick={() => setPhase("history")}
            className="flex-1"
            data-testid="view-history-from-results-btn"
          >
            <Clock size={16} className="mr-2" />
            Historique
          </Button>
        </div>
      </div>
    );
  }

  // ==================== HISTORY PHASE ====================
  if (phase === "history") {
    return (
      <div className="animate-fade-in max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight font-['Outfit']">
              {t("quiz_history")}
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              {tr("Vos résultats passés", "Your past results")}
            </p>
          </div>
          <Button
            onClick={resetQuiz}
            className="bg-[#002FA7] hover:bg-[#002280]"
            data-testid="back-to-quiz-btn"
          >
            <Play size={16} className="mr-2" />
            {t("generate_quiz")}
          </Button>
        </div>

        {history.length === 0 ? (
          <div className="bg-white border border-zinc-200 rounded-sm p-12 text-center">
            <Target size={48} className="mx-auto mb-3 text-zinc-300" />
            <h3 className="font-medium text-zinc-600">{tr("Aucun quiz complété", "No completed quiz")}</h3>
            <p className="text-sm text-zinc-400 mt-1">
              {tr("Lancez votre premier quiz pour commencer", "Start your first quiz to get going")}
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="quiz-history-list">
            {history.map((h) => {
              // Palette daltonien-safe (Vincent 2026-07-25) : bleu (excellent) /
              // ambre (moyen) / orange (faible) — évite le couple rouge/vert.
              const scoreColor =
                h.score_percentage >= 80
                  ? "text-blue-700 bg-blue-50"
                  : h.score_percentage >= 50
                  ? "text-amber-700 bg-amber-50"
                  : "text-orange-700 bg-orange-100";
              // Rythme utilisé (secondes / question) — badge compact affiché
              // à côté de la date. Même palette daltonien-safe que le modal.
              // null si pas de mode Examen (h.time_used_seconds absent).
              let paceBadge = null;
              if (h.time_used_seconds && h.total) {
                const secPerQ = Math.round(h.time_used_seconds / h.total);
                const rushed = secPerQ < 10;
                const rushMinor = secPerQ >= 10 && secPerQ < 20;
                const badgeCls = rushed
                  ? "bg-orange-100 text-orange-900 border-orange-300"
                  : rushMinor
                  ? "bg-amber-100 text-amber-900 border-amber-300"
                  : "bg-blue-100 text-blue-900 border-blue-300";
                const icon = rushed ? "⚠" : rushMinor ? "⏱" : "✓";
                paceBadge = (
                  <span
                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium ${badgeCls}`}
                    title={tr(
                      `Rythme utilisé : ${secPerQ} s/question${rushed ? " (probable manque de temps)" : ""}`,
                      `Pace used: ${secPerQ} s/question${rushed ? " (likely short on time)" : ""}`,
                    )}
                    data-testid={`history-pace-${h.id}`}
                  >
                    <span className="text-xs">{icon}</span>
                    {secPerQ}s/q
                  </span>
                );
              }
              const openDetail = async () => {
                setHistoryDetailLoading(true);
                setHistoryDetail({ ...h, loading: true });
                try {
                  const { data } = await axios.get(`${API}/api/quiz/history/${h.id}`, { withCredentials: true });
                  setHistoryDetail(data);
                } catch (e) {
                  toast.error(tr("Impossible de charger le détail du quiz", "Could not load the quiz details"));
                  setHistoryDetail(null);
                } finally {
                  setHistoryDetailLoading(false);
                }
              };
              return (
                <button
                  key={h.id}
                  type="button"
                  onClick={openDetail}
                  className="w-full bg-white border border-zinc-200 rounded-sm p-4 flex items-center justify-between text-left hover:bg-zinc-50 hover:border-zinc-300 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid={`history-item-${h.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 rounded-sm flex items-center justify-center font-bold text-lg ${scoreColor}`}>
                      {h.score_percentage}%
                    </div>
                    <div>
                      <div className="font-medium text-sm">
                        {h.theme === "all" ? t("all_themes") : h.theme}
                      </div>
                      <div className="text-xs text-zinc-500">
                        {h.score}/{h.total} correctes
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-zinc-400 flex items-center gap-2.5">
                    {paceBadge}
                    <span>{new Date(h.completed_at).toLocaleDateString("fr-FR", {
                      day: "numeric",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}</span>
                    <ArrowRight size={14} className="text-zinc-300" />
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Vincent B555 — Modal détail du quiz */}
        {historyDetail && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            data-testid="quiz-history-detail-modal"
            onClick={(e) => { if (e.target === e.currentTarget) setHistoryDetail(null); }}
          >
            <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
              <header className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold" data-testid="quiz-history-detail-title">
                    Détail du quiz — {historyDetail.theme === "all" ? t("all_themes") : historyDetail.theme}
                  </h3>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    Score : <strong>{historyDetail.score}/{historyDetail.total}</strong> ({historyDetail.score_percentage}%)
                    {" · "}
                    {historyDetail.completed_at && new Date(historyDetail.completed_at).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" })}
                  </p>
                  {/* iter149b — Rythme réellement utilisé (X s/question) pour
                      permettre de discerner un mauvais score dû au manque de
                      temps d'un score dû à une réelle méconnaissance.
                      Palette daltonien-safe (Vincent 2026-07-25) : évite le
                      couple rouge/vert. Utilise orange (alerte) / ambre
                      (moyen) / bleu (confortable), tous distinguables en
                      deutéranopie et protanopie. */}
                  {historyDetail.time_used_seconds && historyDetail.total && (() => {
                    const secPerQ = Math.round(historyDetail.time_used_seconds / historyDetail.total);
                    const totalMin = Math.floor(historyDetail.time_used_seconds / 60);
                    const totalSec = Math.round(historyDetail.time_used_seconds % 60);
                    const timeLimit = historyDetail.time_limit;
                    const wasRushed = secPerQ < 10;
                    const wasRushMinor = secPerQ >= 10 && secPerQ < 20;
                    return (
                      <p
                        className={`text-xs mt-1 inline-flex items-center gap-1.5 px-2 py-0.5 rounded ${
                          wasRushed
                            ? "bg-orange-100 text-orange-900 border border-orange-300"
                            : wasRushMinor
                            ? "bg-amber-100 text-amber-900 border border-amber-300"
                            : "bg-blue-100 text-blue-900 border border-blue-300"
                        }`}
                        data-testid="quiz-history-detail-pace"
                      >
                        <span className="font-bold">{wasRushed ? "⚠" : wasRushMinor ? "⏱" : "✓"}</span>
                        Rythme utilisé : <strong>{secPerQ} s/question</strong>{" "}
                        ({totalMin} min {totalSec.toString().padStart(2, "0")} s
                        {timeLimit ? ` sur ${Math.floor(timeLimit / 60)} min imparti` : ""})
                        {wasRushed && " — probable manque de temps"}
                      </p>
                    );
                  })()}
                </div>
                <button
                  type="button"
                  onClick={() => setHistoryDetail(null)}
                  className="p-1.5 rounded-md hover:bg-zinc-100 text-zinc-500 hover:text-zinc-900"
                  data-testid="quiz-history-detail-close"
                >
                  <XCircle size={20} />
                </button>
              </header>
              <div className="overflow-y-auto flex-1 p-6 space-y-4" data-testid="quiz-history-detail-answers">
                {historyDetailLoading ? (
                  <div className="flex justify-center py-16">
                    <SpinnerGap size={32} className="animate-spin text-blue-500" />
                  </div>
                ) : (historyDetail.answers || []).map((a, idx) => (
                  <div
                    key={a.question_id || idx}
                    className={`border rounded-lg p-4 ${a.is_correct ? "border-blue-200 bg-blue-50/40" : "border-red-200 bg-red-50/40"}`}
                    data-testid={`quiz-history-detail-answer-${idx}`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="text-xs font-mono text-zinc-500">Q{idx + 1}</div>
                      <div className={`flex items-center gap-1 text-xs font-semibold ${a.is_correct ? "text-blue-700" : "text-red-700"}`}>
                        {a.is_correct ? <CheckCircle size={14} weight="fill" /> : <XCircle size={14} weight="fill" />}
                        {a.is_correct ? "+1 point" : "0 point"}
                      </div>
                    </div>
                    <p className="text-sm font-medium text-zinc-900 mb-3">{a.question}</p>
                    <div className="space-y-1.5">
                      {(a.options || []).map((opt, oidx) => {
                        const isSelected = a.selected_option === oidx;
                        const isCorrect = a.correct_option === oidx;
                        let bg = "bg-white border-zinc-200";
                        if (isCorrect) bg = "bg-blue-100 border-blue-400";
                        else if (isSelected && !isCorrect) bg = "bg-red-100 border-red-400";
                        return (
                          <div
                            key={oidx}
                            className={`text-xs px-3 py-2 rounded border ${bg} flex items-center gap-2`}
                          >
                            <span className="font-mono text-zinc-500 w-5">{String.fromCharCode(65 + oidx)}.</span>
                            <span className="flex-1">{opt}</span>
                            {isSelected && <span className="text-[10px] font-semibold uppercase text-zinc-600">{tr("Ta réponse", "Your answer")}</span>}
                            {isCorrect && !isSelected && <span className="text-[10px] font-semibold uppercase text-blue-700">{tr("Bonne réponse", "Correct answer")}</span>}
                            {isCorrect && isSelected && <CheckCircle size={12} weight="fill" className="text-blue-700" />}
                          </div>
                        );
                      })}
                    </div>
                    {a.explanation && (
                      <p className="text-xs text-zinc-600 mt-3 italic border-l-2 border-zinc-300 pl-3">
                        <strong className="not-italic text-zinc-700">Explication : </strong>{a.explanation}
                      </p>
                    )}
                    {a.source?.video_title && (
                      <p className="text-[11px] text-zinc-500 mt-2 flex items-center gap-1">
                        <VideoCamera size={12} /> Source : {a.source.video_title}
                        {a.source.unit_title && ` · ${a.source.unit_title}`}
                      </p>
                    )}
                  </div>
                ))}
                {!historyDetailLoading && (!historyDetail.answers || historyDetail.answers.length === 0) && (
                  <div className="text-center py-8 text-sm text-zinc-500">{tr("Aucune réponse détaillée disponible pour ce quiz.", "No detailed answers available for this quiz.")}</div>
                )}
              </div>
              <footer className="px-6 py-3 border-t border-zinc-200 flex justify-end">
                <Button
                  onClick={() => setHistoryDetail(null)}
                  className="bg-zinc-800 hover:bg-zinc-700 text-white"
                  data-testid="quiz-history-detail-close-btn"
                >
                  {tr("Fermer", "Close")}
                </Button>
              </footer>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
}
